import { Address } from 'viem'
import { ChainId } from '@/domains/chain'
import { getIconPath } from '@/shared/lib/utils/asset.ts'
import { EMPTY_ADDRESS } from '@/shared/constants/address.ts'
import { normalizeAddress } from '@/shared/lib/utils/format.ts'

export type TokenTicker =
  | 'KAIA'
  | 'stKAIA'
  | 'rstKAIA'
  | 'sKlay'
  | 'gcKlay'
  | 'wGCKlay'
  | 'stKLAY'
  | 'LAIR'
  | 'Lr'
  | 'LrBGT'
  | 'BERA'
  | 'WBERA'
  | 'iBGT'
  | 'INJ'
  | 'LsINJ'
  | 'OOGA'
  | 'oogaBGT'
  | 'ZERO'
  | 'zeroBGT'
  | 'infrBGT'
  | 'iBGT_token1'
  | 'HONEY'
  | 'honeyBGT'
  | 'WETH'
  | 'ethBGT'
  | 'WBTC'
  | 'btcBGT'
  | 'PLUTUS'
  | 'pluBGT'
  | 'SOMI'
  | 'stSOMI'

export type TokenTickerGroup = TokenTicker[][]

export const TokenTickers: Record<TokenTicker, TokenTicker> = {
  KAIA: 'KAIA',
  stKAIA: 'stKAIA',
  rstKAIA: 'rstKAIA',
  sKlay: 'sKlay',
  wGCKlay: 'wGCKlay',
  gcKlay: 'gcKlay',
  stKLAY: 'stKLAY',
  LAIR: 'LAIR',
  Lr: 'Lr',
  LrBGT: 'LrBGT',
  BERA: 'BERA',
  WBERA: 'WBERA',
  iBGT: 'iBGT',
  INJ: 'INJ',
  LsINJ: 'LsINJ',
  OOGA: 'OOGA',
  oogaBGT: 'oogaBGT',
  ZERO: 'ZERO',
  zeroBGT: 'zeroBGT',
  infrBGT: 'infrBGT',
  iBGT_token1: 'iBGT_token1',
  HONEY: 'HONEY',
  honeyBGT: 'honeyBGT',
  WETH: 'WETH',
  ethBGT: 'ethBGT',
  WBTC: 'WBTC',
  btcBGT: 'btcBGT',
  PLUTUS: 'PLUTUS',
  pluBGT: 'pluBGT',
  SOMI: 'SOMI',
  stSOMI: 'stSOMI',
} as const

export const TokenNames: Record<TokenTicker, string> = {
  KAIA: 'KAIA',
  stKAIA: 'stKAIA',
  rstKAIA: 'rstKAIA',
  sKlay: 'sKlay',
  wGCKlay: 'wGCKlay',
  gcKlay: 'gcKlay',
  stKLAY: 'stKLAY',
  LAIR: 'LAIR',
  Lr: 'Lr',
  LrBGT: 'LrBGT',
  BERA: 'BERA',
  WBERA: 'WBERA',
  iBGT: 'iBGT',
  INJ: 'INJ',
  LsINJ: 'LsINJ',
  OOGA: 'OOGA',
  oogaBGT: 'oogaBGT',
  ZERO: 'ZERO',
  zeroBGT: 'zeroBGT',
  infrBGT: 'infrBGT',
  iBGT_token1: 'iBGT',
  HONEY: 'HONEY',
  honeyBGT: 'honeyBGT',
  WETH: 'WETH',
  ethBGT: 'ethBGT',
  WBTC: 'WBTC',
  btcBGT: 'btcBGT',
  PLUTUS: 'PLUTUS',
  pluBGT: 'pluBGT',
  SOMI: 'SOMI',
  stSOMI: 'stSOMI',
} as const

export const TokenImages: Record<TokenTicker, string> = {
  KAIA: getIconPath('kaia.png', 'token'),
  stKAIA: getIconPath('stkaia.png', 'token'),
  rstKAIA: getIconPath('rstkaia.png', 'token'),
  sKlay: getIconPath('sklay.png', 'token'),
  wGCKlay: getIconPath('wgcklay.png', 'token'),
  gcKlay: getIconPath('gcklay.png', 'token'),
  stKLAY: getIconPath('stklay.png', 'token'),
  LAIR: getIconPath('lair.png', 'token'),
  Lr: getIconPath('lair.png', 'token'),
  LrBGT: getIconPath('lrbgt.png', 'token'),
  BERA: getIconPath('bera.png', 'token'),
  WBERA: getIconPath('wbera.png', 'token'),
  iBGT: getIconPath('ibgt.png', 'token'),
  INJ: getIconPath('inj.png', 'token'),
  LsINJ: getIconPath('lsinj.png', 'token'),
  OOGA: getIconPath('ooga.png', 'token'),
  oogaBGT: getIconPath('oogaBGT.png', 'token'),
  ZERO: getIconPath('zero.png', 'token'),
  zeroBGT: getIconPath('zeroBGT.png', 'token'),
  infrBGT: getIconPath('infrBGT.png', 'token'),
  iBGT_token1: getIconPath('ibgt.png', 'token'),
  HONEY: getIconPath('honey.png', 'token'),
  honeyBGT: getIconPath('honeyBGT.png', 'token'),
  WETH: getIconPath('weth.png', 'token'),
  ethBGT: getIconPath('ethBGT.png', 'token'),
  WBTC: getIconPath('wbtc.png', 'token'),
  btcBGT: getIconPath('btcBGT.png', 'token'),
  PLUTUS: getIconPath('plutus.png', 'token'),
  pluBGT: getIconPath('pluBGT.png', 'token'),
  SOMI: getIconPath('somi.png', 'token'),
  stSOMI: getIconPath('stSomi.png', 'token'),
} as const

export const TokenAddresses: Record<ChainId, Record<TokenTicker, Address>> = {
  [ChainId.KAIA]: {
    KAIA: normalizeAddress(EMPTY_ADDRESS),
    stKAIA: normalizeAddress('0x42952B873ed6f7f0A7E4992E2a9818E3A9001995'),
    rstKAIA: normalizeAddress('0xd05fCB2d4c427232C3b36a33E21a4F23810A298F'),
    sKlay: normalizeAddress('0xa323d7386b671e8799dca3582d6658fdcdcd940a'),
    wGCKlay: normalizeAddress('0x9b58b9d82b47b9e9fdf76460b8e65df154664cfe'),
    gcKlay: normalizeAddress('0x999999999939Ba65AbB254339eEc0b2A0daC80E9'),
    stKLAY: normalizeAddress('0xF80F2b22932fCEC6189b9153aA18662b15CC9C00'),
    LAIR: normalizeAddress('0xD70C7D511560493C79DF607076fB863f5c8A50b0'),
    Lr: normalizeAddress(EMPTY_ADDRESS),
    LrBGT: normalizeAddress(EMPTY_ADDRESS),
    WBERA: normalizeAddress(EMPTY_ADDRESS),
    BERA: normalizeAddress(EMPTY_ADDRESS),
    iBGT: normalizeAddress(EMPTY_ADDRESS),
    INJ: normalizeAddress(EMPTY_ADDRESS),
    LsINJ: normalizeAddress(EMPTY_ADDRESS),
    OOGA: normalizeAddress(EMPTY_ADDRESS),
    oogaBGT: normalizeAddress(EMPTY_ADDRESS),
    ZERO: normalizeAddress(EMPTY_ADDRESS),
    zeroBGT: normalizeAddress(EMPTY_ADDRESS),
    infrBGT: normalizeAddress(EMPTY_ADDRESS),
    iBGT_token1: normalizeAddress(EMPTY_ADDRESS),
    HONEY: normalizeAddress(EMPTY_ADDRESS),
    honeyBGT: normalizeAddress(EMPTY_ADDRESS),
    WETH: normalizeAddress(EMPTY_ADDRESS),
    ethBGT: normalizeAddress(EMPTY_ADDRESS),
    WBTC: normalizeAddress(EMPTY_ADDRESS),
    btcBGT: normalizeAddress(EMPTY_ADDRESS),
    PLUTUS: normalizeAddress(EMPTY_ADDRESS),
    pluBGT: normalizeAddress(EMPTY_ADDRESS),
    SOMI: normalizeAddress(EMPTY_ADDRESS),
    stSOMI: normalizeAddress(EMPTY_ADDRESS),
  },
  [ChainId.KAIA_TESTNET]: {
    KAIA: normalizeAddress(EMPTY_ADDRESS),
    stKAIA: normalizeAddress('0xe6c96a881383827575db257639736ea0b55ea68d'),
    rstKAIA: normalizeAddress('0xB35a1f39aD1ae7140BFA9E3806Bf9AD18E8f1E0b'),
    sKlay: normalizeAddress(EMPTY_ADDRESS),
    wGCKlay: normalizeAddress(EMPTY_ADDRESS),
    gcKlay: normalizeAddress(EMPTY_ADDRESS),
    stKLAY: normalizeAddress(EMPTY_ADDRESS),
    LAIR: normalizeAddress('0xca5E77CC3b50D26dD8f13F32e97F0695fCc46a46'),
    Lr: normalizeAddress(EMPTY_ADDRESS),
    LrBGT: normalizeAddress(EMPTY_ADDRESS),
    WBERA: normalizeAddress(EMPTY_ADDRESS),
    BERA: normalizeAddress(EMPTY_ADDRESS),
    iBGT: normalizeAddress(EMPTY_ADDRESS),
    INJ: normalizeAddress(EMPTY_ADDRESS),
    LsINJ: normalizeAddress(EMPTY_ADDRESS),
    OOGA: normalizeAddress(EMPTY_ADDRESS),
    oogaBGT: normalizeAddress(EMPTY_ADDRESS),
    ZERO: normalizeAddress(EMPTY_ADDRESS),
    zeroBGT: normalizeAddress(EMPTY_ADDRESS),
    infrBGT: normalizeAddress(EMPTY_ADDRESS),
    iBGT_token1: normalizeAddress(EMPTY_ADDRESS),
    HONEY: normalizeAddress(EMPTY_ADDRESS),
    honeyBGT: normalizeAddress(EMPTY_ADDRESS),
    WETH: normalizeAddress(EMPTY_ADDRESS),
    ethBGT: normalizeAddress(EMPTY_ADDRESS),
    WBTC: normalizeAddress(EMPTY_ADDRESS),
    btcBGT: normalizeAddress(EMPTY_ADDRESS),
    PLUTUS: normalizeAddress(EMPTY_ADDRESS),
    pluBGT: normalizeAddress(EMPTY_ADDRESS),
    SOMI: normalizeAddress(EMPTY_ADDRESS),
    stSOMI: normalizeAddress(EMPTY_ADDRESS),
  },
  [ChainId.BERA]: {
    KAIA: normalizeAddress(EMPTY_ADDRESS),
    stKAIA: normalizeAddress(EMPTY_ADDRESS),
    rstKAIA: normalizeAddress(EMPTY_ADDRESS),
    sKlay: normalizeAddress(EMPTY_ADDRESS),
    wGCKlay: normalizeAddress(EMPTY_ADDRESS),
    gcKlay: normalizeAddress(EMPTY_ADDRESS),
    stKLAY: normalizeAddress(EMPTY_ADDRESS),
    LAIR: normalizeAddress('0xf3530788DEB3d21E8fA2c3CBBF93317FB38a0D3C'),
    Lr: normalizeAddress(EMPTY_ADDRESS),
    LrBGT: normalizeAddress('0x66611Ba2aa5deB46e6138aD21a202b40ecE5b6AB'),
    WBERA: normalizeAddress('0x6969696969696969696969696969696969696969'),
    BERA: normalizeAddress(EMPTY_ADDRESS),
    iBGT: normalizeAddress('0xac03caba51e17c86c921e1f6cbfbdc91f8bb2e6b'),
    INJ: normalizeAddress(EMPTY_ADDRESS),
    LsINJ: normalizeAddress(EMPTY_ADDRESS),
    OOGA: normalizeAddress('0x009af46df68db0e76bfe9ea35663f6ed17877956'),
    oogaBGT: normalizeAddress('0x4a110b149A9A6aBB1F4C83b20B3f6c4BBd4A1019'),
    ZERO: normalizeAddress('0x0000382FbB422B4f593931FC6f2d25CC16600000'),
    zeroBGT: normalizeAddress('0xdeB193207C1F9482b340551fe2fb25390a218e31'),
    infrBGT: normalizeAddress('0x77dF4e613aBDc31522af8aFA24aE0a04Bc8c9aAA'),
    iBGT_token1: normalizeAddress('0xac03caba51e17c86c921e1f6cbfbdc91f8bb2e6b'),
    HONEY: normalizeAddress('0xFCBD14DC51f0A4d49d5E53C2E0950e0bC26d0Dce'),
    honeyBGT: normalizeAddress('0xd5c3B4c49625951b2ddFaE5bEF4b173961192870'),
    WETH: normalizeAddress('0x2F6F07CDcf3588944Bf4C42aC74ff24bF56e7590'),
    ethBGT: normalizeAddress('0x55B04504748E949B2c6981375467435707579A1F'),
    WBTC: normalizeAddress('0x0555E30da8f98308EdB960aa94C0Db47230d2B9c'),
    btcBGT: normalizeAddress('0xCc313C1ED7C244688d81e683dEc50b2546e5BD1d'),
    PLUTUS: normalizeAddress('0x6698EDe14709A9Dd8379744CC2D28F276E3d2Eac'),
    pluBGT: normalizeAddress('0xf27Df3d289C3D802CA2A3d23Db85d72d146D2e9a'),
    SOMI: normalizeAddress(EMPTY_ADDRESS),
    stSOMI: normalizeAddress(EMPTY_ADDRESS),
  },
  [ChainId.BERA_TESTNET]: {
    KAIA: normalizeAddress(EMPTY_ADDRESS),
    stKAIA: normalizeAddress(EMPTY_ADDRESS),
    rstKAIA: normalizeAddress(EMPTY_ADDRESS),
    sKlay: normalizeAddress(EMPTY_ADDRESS),
    wGCKlay: normalizeAddress(EMPTY_ADDRESS),
    gcKlay: normalizeAddress(EMPTY_ADDRESS),
    stKLAY: normalizeAddress(EMPTY_ADDRESS),
    LAIR: normalizeAddress(EMPTY_ADDRESS),
    Lr: normalizeAddress(EMPTY_ADDRESS),
    LrBGT: normalizeAddress(EMPTY_ADDRESS),
    WBERA: normalizeAddress('0x6969696969696969696969696969696969696969'),
    BERA: normalizeAddress(EMPTY_ADDRESS),
    iBGT: normalizeAddress(EMPTY_ADDRESS),
    INJ: normalizeAddress(EMPTY_ADDRESS),
    LsINJ: normalizeAddress(EMPTY_ADDRESS),
    OOGA: normalizeAddress(EMPTY_ADDRESS),
    oogaBGT: normalizeAddress(EMPTY_ADDRESS),
    ZERO: normalizeAddress(EMPTY_ADDRESS),
    zeroBGT: normalizeAddress(EMPTY_ADDRESS),
    infrBGT: normalizeAddress(EMPTY_ADDRESS),
    iBGT_token1: normalizeAddress(EMPTY_ADDRESS),
    HONEY: normalizeAddress(EMPTY_ADDRESS),
    honeyBGT: normalizeAddress(EMPTY_ADDRESS),
    WETH: normalizeAddress(EMPTY_ADDRESS),
    ethBGT: normalizeAddress(EMPTY_ADDRESS),
    WBTC: normalizeAddress(EMPTY_ADDRESS),
    btcBGT: normalizeAddress(EMPTY_ADDRESS),
    PLUTUS: normalizeAddress(EMPTY_ADDRESS),
    pluBGT: normalizeAddress(EMPTY_ADDRESS),
    SOMI: normalizeAddress(EMPTY_ADDRESS),
    stSOMI: normalizeAddress(EMPTY_ADDRESS),
  },
  [ChainId.INJECTIVE_TESTNET]: {
    KAIA: normalizeAddress(EMPTY_ADDRESS),
    stKAIA: normalizeAddress(EMPTY_ADDRESS),
    rstKAIA: normalizeAddress(EMPTY_ADDRESS),
    sKlay: normalizeAddress(EMPTY_ADDRESS),
    wGCKlay: normalizeAddress(EMPTY_ADDRESS),
    gcKlay: normalizeAddress(EMPTY_ADDRESS),
    stKLAY: normalizeAddress(EMPTY_ADDRESS),
    LAIR: normalizeAddress(EMPTY_ADDRESS),
    Lr: normalizeAddress(EMPTY_ADDRESS),
    LrBGT: normalizeAddress(EMPTY_ADDRESS),
    WBERA: normalizeAddress(EMPTY_ADDRESS),
    BERA: normalizeAddress(EMPTY_ADDRESS),
    iBGT: normalizeAddress(EMPTY_ADDRESS),
    INJ: normalizeAddress(EMPTY_ADDRESS),
    LsINJ: normalizeAddress('0x494401396FD1cf51cDD13e29eCFA769F49e1F5D3'),
    OOGA: normalizeAddress(EMPTY_ADDRESS),
    oogaBGT: normalizeAddress(EMPTY_ADDRESS),
    ZERO: normalizeAddress(EMPTY_ADDRESS),
    zeroBGT: normalizeAddress(EMPTY_ADDRESS),
    infrBGT: normalizeAddress(EMPTY_ADDRESS),
    iBGT_token1: normalizeAddress(EMPTY_ADDRESS),
    HONEY: normalizeAddress(EMPTY_ADDRESS),
    honeyBGT: normalizeAddress(EMPTY_ADDRESS),
    WETH: normalizeAddress(EMPTY_ADDRESS),
    ethBGT: normalizeAddress(EMPTY_ADDRESS),
    WBTC: normalizeAddress(EMPTY_ADDRESS),
    btcBGT: normalizeAddress(EMPTY_ADDRESS),
    PLUTUS: normalizeAddress(EMPTY_ADDRESS),
    pluBGT: normalizeAddress(EMPTY_ADDRESS),
    SOMI: normalizeAddress(EMPTY_ADDRESS),
    stSOMI: normalizeAddress(EMPTY_ADDRESS),
  },
  [ChainId.SOMNIA]: {
    KAIA: normalizeAddress(EMPTY_ADDRESS),
    stKAIA: normalizeAddress(EMPTY_ADDRESS),
    rstKAIA: normalizeAddress(EMPTY_ADDRESS),
    sKlay: normalizeAddress(EMPTY_ADDRESS),
    wGCKlay: normalizeAddress(EMPTY_ADDRESS),
    gcKlay: normalizeAddress(EMPTY_ADDRESS),
    stKLAY: normalizeAddress(EMPTY_ADDRESS),
    LAIR: normalizeAddress(EMPTY_ADDRESS),
    Lr: normalizeAddress(EMPTY_ADDRESS),
    LrBGT: normalizeAddress(EMPTY_ADDRESS),
    WBERA: normalizeAddress(EMPTY_ADDRESS),
    BERA: normalizeAddress(EMPTY_ADDRESS),
    iBGT: normalizeAddress(EMPTY_ADDRESS),
    INJ: normalizeAddress(EMPTY_ADDRESS),
    LsINJ: normalizeAddress(EMPTY_ADDRESS),
    OOGA: normalizeAddress(EMPTY_ADDRESS),
    oogaBGT: normalizeAddress(EMPTY_ADDRESS),
    ZERO: normalizeAddress(EMPTY_ADDRESS),
    zeroBGT: normalizeAddress(EMPTY_ADDRESS),
    infrBGT: normalizeAddress(EMPTY_ADDRESS),
    iBGT_token1: normalizeAddress(EMPTY_ADDRESS),
    HONEY: normalizeAddress(EMPTY_ADDRESS),
    honeyBGT: normalizeAddress(EMPTY_ADDRESS),
    WETH: normalizeAddress(EMPTY_ADDRESS),
    ethBGT: normalizeAddress(EMPTY_ADDRESS),
    WBTC: normalizeAddress(EMPTY_ADDRESS),
    btcBGT: normalizeAddress(EMPTY_ADDRESS),
    PLUTUS: normalizeAddress(EMPTY_ADDRESS),
    pluBGT: normalizeAddress(EMPTY_ADDRESS),
    SOMI: normalizeAddress(EMPTY_ADDRESS),
    stSOMI: normalizeAddress('0x66dA8f12CEA987BF79F1c3b278cdAe741C1a1A75'),
  },
  [ChainId.SOMNIA_TESTNET]: {
    KAIA: normalizeAddress(EMPTY_ADDRESS),
    stKAIA: normalizeAddress(EMPTY_ADDRESS),
    rstKAIA: normalizeAddress(EMPTY_ADDRESS),
    sKlay: normalizeAddress(EMPTY_ADDRESS),
    wGCKlay: normalizeAddress(EMPTY_ADDRESS),
    gcKlay: normalizeAddress(EMPTY_ADDRESS),
    stKLAY: normalizeAddress(EMPTY_ADDRESS),
    LAIR: normalizeAddress(EMPTY_ADDRESS),
    Lr: normalizeAddress(EMPTY_ADDRESS),
    LrBGT: normalizeAddress(EMPTY_ADDRESS),
    WBERA: normalizeAddress(EMPTY_ADDRESS),
    BERA: normalizeAddress(EMPTY_ADDRESS),
    iBGT: normalizeAddress(EMPTY_ADDRESS),
    INJ: normalizeAddress(EMPTY_ADDRESS),
    LsINJ: normalizeAddress(EMPTY_ADDRESS),
    OOGA: normalizeAddress(EMPTY_ADDRESS),
    oogaBGT: normalizeAddress(EMPTY_ADDRESS),
    ZERO: normalizeAddress(EMPTY_ADDRESS),
    zeroBGT: normalizeAddress(EMPTY_ADDRESS),
    infrBGT: normalizeAddress(EMPTY_ADDRESS),
    iBGT_token1: normalizeAddress(EMPTY_ADDRESS),
    HONEY: normalizeAddress(EMPTY_ADDRESS),
    honeyBGT: normalizeAddress(EMPTY_ADDRESS),
    WETH: normalizeAddress(EMPTY_ADDRESS),
    ethBGT: normalizeAddress(EMPTY_ADDRESS),
    WBTC: normalizeAddress(EMPTY_ADDRESS),
    btcBGT: normalizeAddress(EMPTY_ADDRESS),
    PLUTUS: normalizeAddress(EMPTY_ADDRESS),
    pluBGT: normalizeAddress(EMPTY_ADDRESS),
    SOMI: normalizeAddress(EMPTY_ADDRESS),
    stSOMI: normalizeAddress('0x669C741814c5Ce831f43aa954bBbF701Ae367F20'),
  },
} as const

export const LiquidTokenDescriptions: Partial<Record<TokenTicker, string>> = {
  stKAIA: 'Lair Staked KAIA',
  rstKAIA: 'Lair Restaked KAIA',
  sKlay: 'KLAYstation Staked KAIA',
  gcKlay: 'Swapscanner Staked KAIA',
  stKLAY: 'Stake.ly Staked KAIA',
} as const

export const NativeTokenTickers: Record<ChainId, TokenTicker> = {
  [ChainId.KAIA]: TokenTickers.KAIA,
  [ChainId.KAIA_TESTNET]: TokenTickers.KAIA,
  [ChainId.BERA]: TokenTickers.BERA,
  [ChainId.BERA_TESTNET]: TokenTickers.BERA,
  [ChainId.INJECTIVE_TESTNET]: TokenTickers.INJ,
  [ChainId.SOMNIA]: TokenTickers.SOMI,
} as const
